import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import "./manageClasses.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faFilter,
  faSortAmountDown,
  faUsers,
  faChalkboardTeacher,
  faBookOpen,
  faCalendarCheck,
  faArrowRight,
  faSpinner,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

const ManageClasses = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState([
    { icon: faUsers, value: "Loading...", label: "Total Students" },
    { icon: faChalkboardTeacher, value: "Loading...", label: "Active Teachers" },
    { icon: faBookOpen, value: "14", label: "Classes" },
    { icon: faCalendarCheck, value: "96.2%", label: "Attendance Records" },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";
  
  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const imageFilenames = [
    "pg.png",
    "nur.png",
    "prep.png",
    "1.png",
    "2.png",
    "3.png",
    "4.png",
    "5.png",
    "6.png",
    "7.png",
    "8.png",
    "8.png",
    "9.png",
    "10.png",
  ];

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch student stats
      const studentResponse = await axios.get(`${API_BASE_URL}/stdSummary.php`);
      
      // Fetch teacher stats
      const teacherResponse = await axios.get(`${API_BASE_URL}/TeacherStats.php`);
      
      if (studentResponse.data.success && teacherResponse.data.success) {
        const studentData = studentResponse.data.data;
        const teacherData = teacherResponse.data.data;
        
        // Format numbers with commas
        const formatNumber = (num) => {
          return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        };
        
        setStats([
          { 
            icon: faUsers, 
            value: formatNumber(studentData.total_students || studentData.total_valid_records || 0), 
            label: "Total Students",
            rawValue: studentData.total_students || studentData.total_valid_records || 0
          },
          { 
            icon: faChalkboardTeacher, 
            value: formatNumber(teacherData.active_teachers || teacherData.total_teachers || 0), 
            label: "Active Teachers",
            rawValue: teacherData.active_teachers || teacherData.total_teachers || 0
          },
          { 
            icon: faBookOpen, 
            value: classes.length.toString(), 
            label: "Classes",
            rawValue: classes.length
          },
          { 
            icon: faCalendarCheck, 
            value: "100%", 
            label: "Attendance Records",
            rawValue: 96.2
          },
        ]);
      } else {
        // Use fallback data if available
        const studentFallback = studentResponse.data.fallback_data;
        const teacherFallback = teacherResponse.data.fallback_data;
        
        if (studentFallback && teacherFallback) {
          setStats([
            { icon: faUsers, value: studentFallback.total_students.toString(), label: "Total Students" },
            { icon: faChalkboardTeacher, value: teacherFallback.active_teachers.toString(), label: "Active Teachers" },
            { icon: faBookOpen, value: "14", label: "Classes" },
            { icon: faCalendarCheck, value: "100%", label: "Attendance Rate" },
          ]);
        } else {
          throw new Error("Failed to fetch dashboard statistics");
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setError("Unable to load statistics. Showing sample data.");
      
      // Set fallback sample data
      setStats([
        { icon: faUsers, value: "1,428", label: "Total Students" },
        { icon: faChalkboardTeacher, value: "48", label: "Active Teachers" },
        { icon: faBookOpen, value: "14", label: "Classes" },
        { icon: faCalendarCheck, value: "100%", label: "Attendance Records" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = classes.filter((className) =>
    className.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClassClick = (className) => {
    navigate("/Classes-Attendance", { state: { className } });
  };

  const handleAddClass = () => {
    console.log("Add new class clicked");
  };

  const handleViewAllClasses = () => {
    console.log("View all classes clicked");
  };

  const handleRefreshStats = () => {
    fetchDashboardStats();
  };

  return (
    <div className="manage-classes-screen">
      <div className="manage-classes-dashboard-wrapper">
        <Sidebar />
        <main className="manage-classes-main-content">
          {/* Header Section */}
          <div className="manage-classes-header">
            <div className="manage-classes-header-content">
              <h2 className="manage-classes-title">
                <FontAwesomeIcon
                  icon={faBookOpen}
                  className="manage-classes-title-icon"
                />
                Class Management Portal
              </h2>
              <p className="manage-classes-subtitle">
                Manage and monitor all classes, track attendance, and oversee
                academic progress from a single dashboard.
              </p>
            </div>
            
            {/* Refresh Button */}
            <button 
              className="manage-classes-refresh-btn"
              onClick={handleRefreshStats}
              disabled={loading}
            >
              <FontAwesomeIcon 
                icon={loading ? faSpinner : faFilter} 
                spin={loading}
              />
              {loading ? " Refreshing..." : " Refresh Data"}
            </button>
          </div>

         
          {/* Search Bar */}
          <div className="manage-classes-search-container">
            <div className="manage-classes-search-icon">
              <FontAwesomeIcon icon={faSearch} />
            </div>
            <input
              type="text"
              className="manage-classes-search-input"
              placeholder="Search classes by name, grade, or teacher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="manage-classes-error-alert">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </div>
          )}

          {/* Stats Overview */}
          <div className="manage-classes-stats-overview">
            {stats.map((stat, index) => (
              <div 
                className={`manage-classes-stat-card ${index === 3 ? 'attendance-card-hover' : ''}`} 
                key={index}
              >
                <div className="manage-classes-stat-content">
                  <div className="manage-classes-stat-icon">
                    {loading ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={stat.icon} />
                    )}
                  </div>
                  <div className="manage-classes-stat-details">
                    <div className="manage-classes-stat-value">
                      {loading ? "..." : stat.value}
                    </div>
                    <div className="manage-classes-stat-label">
                      {stat.label}
                    </div>
                  </div>
                </div>
                
                {/* Tooltips for the first two cards */}
                {index === 0 && stat.rawValue > 0 && (
                  <div className="manage-classes-stat-tooltip">
                    <span className="tooltip-text">
                      {stat.rawValue} registered students
                    </span>
                  </div>
                )}
                {index === 1 && stat.rawValue > 0 && (
                  <div className="manage-classes-stat-tooltip">
                    <span className="tooltip-text">
                      {stat.rawValue} active teaching staff
                    </span>
                  </div>
                )}
                
                {/* View button overlay for Attendance Rate card (index 3) */}
                {index === 3 && (
                  <div className="attendance-view-overlay">
                    <button 
                      className="attendance-view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/Attendance Records");
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                      View Records
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Classes Grid Header */}
          <div className="manage-classes-grid-header">
            <FontAwesomeIcon
              icon={faBookOpen}
              className="manage-classes-grid-icon"
            />
            <h3 className="manage-classes-grid-title">
              All Classes ({filteredClasses.length})
            </h3>
            {loading && (
              <div className="manage-classes-loading-indicator">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Updating data...</span>
              </div>
            )}
          </div>

          {/* Classes Grid */}
          <div className="manage-classes-grid-container">
            {filteredClasses.map((className, index) => (
              <div
                className="manage-classes-card"
                key={index}
                onClick={() => handleClassClick(className)}
              >
                <div className="manage-classes-card-top">
                  <img
                    src={`/Classes/${imageFilenames[index]}`}
                    alt={className}
                    className="manage-classes-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `/Classes/default.png`;
                    }}
                  />
                </div>
                <div className="manage-classes-card-bottom">
                  <h3 className="manage-classes-card-title">{className}</h3>
                  <p className="manage-classes-card-grade">
                    {className.includes("Play Group") ||
                    className.includes("Nursery") ||
                    className.includes("Prep")
                      ? "Pre-School Level"
                      : `Grade ${className.replace("Class ", "")}`}
                  </p>
                </div>
                <div className="manage-classes-action-overlay">
                  <button className="manage-classes-view-btn">
                    <span className="manage-classes-btn-text">
                      View Details
                    </span>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="manage-classes-btn-icon"
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer Info */}
          <div className="manage-classes-footer">
            <p className="manage-classes-footer-text">
              Showing {filteredClasses.length} of {classes.length} classes •
              <a href="#" className="manage-classes-footer-link">
                View class analytics
              </a>{" "}
              •
              <a href="#" className="manage-classes-footer-link">
                Export class list
              </a>
            </p>
            <p className="manage-classes-footer-note">
              Click on any class card to view detailed attendance and management
              options
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManageClasses;